"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ALL_COMMANDS,
  ALL_PROVIDERS,
  ALL_SCOPES,
  scopeFromPathname,
} from "./registry";
import { rank } from "./search/rank";
import type { FeatureRender, PaletteCtx, Result } from "./types";

// =====================================================================
// Query parsing
// =====================================================================

type ParsedQuery = {
  prefix: "command" | "provider" | "help" | "none";
  providerId?: string;
  text: string;
};

export function parseQuery(input: string): ParsedQuery {
  const trimmed = input.trim();
  if (!trimmed) return { prefix: "none", text: "" };
  if (trimmed.startsWith(">")) return { prefix: "command", text: trimmed.slice(1).trim() };
  if (trimmed === "?") return { prefix: "help", text: "" };
  if (trimmed.startsWith("@")) {
    const rest = trimmed.slice(1);
    const space = rest.indexOf(" ");
    const providerId = space < 0 ? rest : rest.slice(0, space);
    const text = space < 0 ? "" : rest.slice(space + 1).trim();
    return { prefix: "provider", providerId, text };
  }
  return { prefix: "none", text: trimmed };
}

// =====================================================================
// Resolver hook
// =====================================================================

const PROVIDER_DEBOUNCE_MS = 150;

interface ProviderAnswer {
  input: string;
  ctx: PaletteCtx;
  results: Result[];
}

export function useResolver(input: string, ctx: PaletteCtx) {
  // The currentScopeId boost is route-driven, so it's safe to compute once
  // per pathname change.
  const currentScopeId = useMemo(
    () => scopeFromPathname(ctx.pathname)?.id ?? null,
    [ctx.pathname],
  );

  const parsed = useMemo(() => parseQuery(input), [input]);

  // Synchronous pass — scopes + commands. Empty-query: no scopes (we
  // don't want to wallpaper the screen with every route); commands are
  // still useful even with no query because the user often wants the
  // "things I can do here" list.
  const { syncScopes, syncCommands } = useMemo(() => {
    const noSync = parsed.prefix === "provider" || parsed.prefix === "help";
    return {
      syncScopes: noSync || !parsed.text ? [] : ALL_SCOPES.filter((s) => s.href),
      syncCommands: noSync ? [] : ALL_COMMANDS,
    };
  }, [parsed]);

  // The providers this query reaches: none for help or an empty unprefixed
  // query, the named one for an @-prefix, every enabled one otherwise.
  const providers = useMemo(() => {
    if (parsed.prefix === "help") return [];
    if (parsed.text.length < 1 && parsed.prefix !== "provider") return [];
    const candidates =
      parsed.prefix === "provider"
        ? ALL_PROVIDERS.filter(
            (p) =>
              p.prefix?.replace(/^@/, "") === parsed.providerId ||
              p.id === parsed.providerId,
          )
        : ALL_PROVIDERS;
    return candidates.filter((p) => (p.enabled ? p.enabled(ctx) : true));
  }, [parsed, ctx]);

  // Async pass — an answer is kept with the exact input and context it
  // answers, so a stale answer can never render under a newer query.
  const [answer, setAnswer] = useState<ProviderAnswer | null>(null);
  useEffect(() => {
    if (providers.length === 0) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const settled = await Promise.allSettled(
        providers.map((p) =>
          p.search(parsed.text, controller.signal, ctx).catch(() => [] as Result[]),
        ),
      );
      if (controller.signal.aborted) return;
      setAnswer({
        input,
        ctx,
        results: settled.flatMap((r) => (r.status === "fulfilled" ? r.value : [])),
      });
    }, PROVIDER_DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [providers, parsed.text, input, ctx]);

  const asyncResults =
    answer && answer.input === input && answer.ctx === ctx ? answer.results : null;

  const groups = useMemo<FeatureRender[]>(
    () =>
      rank({
        query: parsed.text,
        scopes: syncScopes,
        commands: syncCommands,
        asyncResults: asyncResults ?? [],
        currentScopeId,
      }),
    [parsed.text, syncScopes, syncCommands, asyncResults, currentScopeId],
  );

  return { groups, loading: providers.length > 0 && asyncResults === null };
}
